
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCart";
import { toast } from "@/hooks/use-toast";
import { fetchProducts, seedSpecificProducts, type Product } from "@/services/productService";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import SearchBar from "@/components/menu/SearchBar";
import MenuCategoryTabs from "@/components/menu/MenuCategoryTabs";
import CartSummary from "@/components/menu/CartSummary";
import { ProductDetail } from "@/components/menu/ProductDetail";

const Menu = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, addToCart, getTotalPrice, getTotalItems } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const result = await fetchProducts();
      
      // Filter out "Adicionais" category
      const filteredProducts = result.products.filter(
        product => !product.category?.trim().toLowerCase().includes("adicionai")
      );
      setProducts(filteredProducts);
      
      // Ensure categories are sorted in the order: Lanches, Pratos, Porções, Bebidas
      const categoryOrder = ["Lanches", "Pratos", "Porções", "Bebidas"];
      const sortedCategories = ["Todos"]
        .concat(
          result.categories
            .filter(c => c !== "Todos" && !c?.trim().toLowerCase().includes("adicionai"))
            .sort((a, b) => {
              const aIndex = categoryOrder.indexOf(a);
              const bIndex = categoryOrder.indexOf(b);
              
              // If both categories are in the order array, sort by their position
              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }
              
              // If only one is in the order array, prioritize it
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              
              // If neither is in the order array, sort alphabetically
              return a.localeCompare(b);
            })
        );
        
      setCategories(sortedCategories);
      
      if (result.error) {
        toast({
          title: "Erro ao carregar produtos",
          description: "Usando dados locais em vez de dados do banco",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error in loadProducts:", error);
      toast({
        title: "Erro ao carregar produtos",
        description: "Não foi possível carregar o menu",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter products by category, search query and active status (only admins can see paused products)
  const filteredProducts = products.filter(product => {
    const isAdmin = user?.app_metadata?.role === "admin";
    return (
      (selectedCategory === "Todos" || product.category === selectedCategory) && 
      (product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
       product.description.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (isAdmin || product.is_active) // Only show active products to regular users
    );
  });

  // Order for "Todos": Lanches -> Pratos -> Porções -> Bebidas -> others
  const categoryRank = (cat: string = "") => {
    const c = (cat || "").toLowerCase();
    if (c.includes("lanche") || c.includes("xis")) return 0; // treat Xis as lanches
    if (c.includes("prato")) return 1;
    if (c.includes("porç") || c.includes("porcao") || c.includes("porcao") || c.includes("porcoes")) return 2;
    if (c.includes("bebid")) return 3;
    return 4;
  };

  const orderedFilteredProducts = [...filteredProducts].sort((a, b) => {
    const ra = categoryRank(a.category);
    const rb = categoryRank(b.category);
    if (ra !== rb) return ra - rb;
    
    // Within lanches (rank 0), prioritize xis salada and xis bacon first, then Bauru last
    if (ra === 0) {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      
      // First: Xis Salada and Xis Bacon
      if (aName.includes('xis salada')) return -1;
      if (bName.includes('xis salada')) return 1;
      if (aName.includes('xis bacon')) return -1;
      if (bName.includes('xis bacon')) return 1;
      
      // Last: Bauru items
      if (aName.startsWith('bauru') && !bName.startsWith('bauru')) return 1;
      if (!aName.startsWith('bauru') && bName.startsWith('bauru')) return -1;
    }
    
    return a.name.localeCompare(b.name);
  });

  const productsForView = selectedCategory === "Todos" ? orderedFilteredProducts : filteredProducts;
  
  const handleAddToCart = (product: Product) => {
    addToCart(product);
    toast({
      title: "Produto adicionado",
      description: `${product.name} adicionado ao carrinho`
    });
  };

  const handleViewDetails = (product: Product) => {
    setSelectedProduct(product);
    setIsProductDetailOpen(true);
  };

  const proceedToCheckout = () => {
    if (cart.length === 0) {
      toast({
        title: "Carrinho vazio",
        description: "Adicione produtos ao carrinho antes de continuar",
        variant: "destructive"
      });
      return;
    }
    navigate("/checkout");
  };

  // Load menu items if none exist
  useEffect(() => {
    if (products.length === 0 && !loading) {
      const seedProducts = async () => {
        const success = await seedSpecificProducts();
        if (success) {
          loadProducts(); // Refetch products
        }
      };
      seedProducts();
    }
  }, [products, loading]);

  // Fix for back button - go directly to home page instead of browser history
  const handleBackButton = () => {
    navigate("/");
  };

  // Force a refresh of the products from the database to make sure we have the latest data
  const handleForceRefresh = () => {
    loadProducts();
    toast({
      title: "Menu atualizado",
      description: "Os produtos foram atualizados com sucesso"
    });
  };


  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <Navbar />
      
      <div className="pt-24 sm:pt-28 pb-16 relative">
        {/* Background image com 95% de opacidade no light */}
        <div 
          className="absolute inset-0 opacity-95 dark:opacity-30 bg-cover bg-center bg-fixed transition-opacity duration-300"
          style={{
            backgroundImage: "url('https://i.postimg.cc/6pwc2KZ7/vecteezy-hamburger-backgrounds-for-graphic-design-inspiration-46349071.jpg')",
            zIndex: 0
          }}
        />
        {/* Overlay adaptativo para light e dark */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/35 to-background/90 dark:from-black/50 dark:via-black/70 dark:to-black/95 transition-all duration-300 pointer-events-none" />
        
        {/* Content overlay */}
        <div className="relative z-10">
          <div className="container mx-auto px-4">
            <Button 
              variant="ghost" 
              onClick={handleBackButton} 
              className="mb-4 text-white hover:text-krecao-yellow flex items-center gap-1 bg-krecao-red rounded-lg"
            >
              <ArrowLeft size={16} />
              Voltar
            </Button>
            
            <div className="mb-8 text-center pt-2">
              <h1 className="text-4xl md:text-5xl font-bold mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                <span className="text-krecao-yellow font-extrabold [text-shadow:_0_2px_4px_rgba(0,0,0,0.7)]">Car</span>
                <span className="text-foreground font-extrabold">dá</span>
                <span className="text-krecao-red font-extrabold">pio</span>
                <span className="text-krecao-yellow font-extrabold [text-shadow:_0_2px_4px_rgba(0,0,0,0.7)]"> Promocional</span>
              </h1>
              <p className="text-gray-800 dark:text-gray-300 mb-4 max-w-md mx-auto font-medium drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)] dark:drop-shadow-md">
                Escolha seus lanches favoritos preparados com ingredientes frescos e de qualidade
              </p>
              
              
              <SearchBar isSearchVisible={isSearchVisible} searchQuery={searchQuery} setSearchQuery={setSearchQuery} setIsSearchVisible={setIsSearchVisible} />

              <MenuCategoryTabs 
                categories={categories} 
                selectedCategory={selectedCategory} 
                setSelectedCategory={setSelectedCategory} 
                filteredProducts={productsForView} 
                onAddToCart={handleAddToCart} 
                onViewDetails={handleViewDetails}
                loading={loading} 
              />
            </div>
          </div>

          {cart.length > 0 && (
            <CartSummary 
              totalItems={getTotalItems()} 
              totalPrice={getTotalPrice()} 
              onCheckout={proceedToCheckout} 
            />
          )}

          <ProductDetail
            product={selectedProduct}
            isOpen={isProductDetailOpen}
            onClose={() => {
              setIsProductDetailOpen(false);
              setSelectedProduct(null);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Menu;
